import { z } from "zod";

export const CaptureType = z.enum(["rencontre", "idee_flash"]);
export type CaptureType = z.infer<typeof CaptureType>;

export const MeetingAttendeeSchema = z.object({
  name: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
});
export type MeetingAttendee = z.infer<typeof MeetingAttendeeSchema>;

export const MeetingContextSchema = z.object({
  event_id: z.string(),
  title: z.string().nullable().default(null),
  description: z.string().nullable().default(null),
  attendees: z.array(MeetingAttendeeSchema).default([]),
  start_iso: z.string(),
  end_iso: z.string(),
  html_link: z.string().nullable().default(null),
});
export type MeetingContext = z.infer<typeof MeetingContextSchema>;

export const TranscriptSegmentSchema = z.object({
  speaker: z.string().min(1).max(120),
  text: z.string(),
});
export type TranscriptSegment = z.infer<typeof TranscriptSegmentSchema>;

export const CaptureStatus = z.enum([
  "recording",
  "uploading",
  // Dernier recours serveur (spec anti-perte phase 1, §5.2.2) : la fusion a
  // échoué deux fois sur le téléphone, les segments sont envoyés un à un et le
  // serveur assemble. `assembling` = en cours (affiché « En préparation ») ;
  // `assembly_failed` = l'assemblage serveur a échoué (« Problème signalé »,
  // rien n'est effacé, une nouvelle tentative reste possible).
  "assembling",
  "assembly_failed",
  // État de repos : audio synchronisé et conservé, mais transcription/analyse
  // PAS demandée (défaut). L'usager lance l'analyse à la demande → le flux
  // reprend captured → transcribing → processing → ready.
  "captured",
  "transcribing",
  "processing",
  "ready",
  "error",
]);
export type CaptureStatus = z.infer<typeof CaptureStatus>;

export const CaptureSchema = z
  .object({
    id: z.uuid(),
    createdBy: z.uuid(),
    projectId: z.uuid(),
    type: CaptureType,
    title: z.string().min(1).max(200).nullable(),
    recordedAt: z.iso.datetime(),
    durationS: z.number().int().nonnegative(),
    audioPath: z.string().min(1).nullable(),
    keepAudio: z.boolean(),
    status: CaptureStatus,
    transcriptRaw: z.string().nullable().default(null),
    transcriptSegments: z.array(TranscriptSegmentSchema).nullable().default(null),
    summary: z.string().nullable().default(null),
    // Phrase-cœur de la discussion, générée par la synthèse et dénormalisée sur
    // captures pour la liste d'accueil (LIST_COLUMNS la tire sans jointure).
    gist: z.string().nullable().default(null),
    // Présence d'un transcript SANS tirer son texte. La liste (page d'accueil)
    // n'embarque plus transcript_raw/segments/summary (egress) ; ce booléen,
    // alimenté par la colonne générée `has_transcript`, suffit au badge
    // d'extraction. Optionnel : absent des captures construites côté client
    // (nouvelle capture) ou d'un contexte mono-capture pré-migration, où l'on
    // retombe sur la présence de transcriptRaw.
    hasTranscript: z.boolean().optional(),
    // Dernière édition manuelle de la transcription (item « Modifier une
    // transcription ») — sert à signaler qu'une analyse est antérieure à la
    // version affichée du texte. Optionnel comme hasTranscript : absent des
    // captures construites côté client et des lectures qui ne sélectionnent
    // pas la colonne ; null si jamais modifiée.
    transcriptEditedAt: z.iso.datetime().nullable().optional(),
    errorMessage: z.string().nullable().default(null),
    // Anti-perte (spec phase 1, §5.2.3) : durée d'horloge de la prise (colonne
    // `elapsed_s`) et durée estimée d'audio illisible (`missing_audio_ms`), qui
    // font l'état « Incomplète ». Optionnels : absents des captures d'avant et
    // des lectures qui ne sélectionnent pas ces colonnes ; null si inconnus.
    elapsedS: z.number().int().nonnegative().nullable().optional(),
    missingAudioMs: z.number().int().nonnegative().nullable().optional(),
    meetingContext: MeetingContextSchema.nullable().default(null),
    // Step 11 Palier 2 : route extract via OpenAI Batch API (-50 % coût, ~24 h).
    // True par défaut côté UI ("Je ne suis pas pressé") — la plupart des captures
    // n'ont pas besoin d'items immédiats. False pour extract sync temps réel.
    batchExtraction: z.boolean().default(true),
    // Approbation manuelle override du verdict scorecard (créateur-only via RLS).
    approvedAt: z.iso.datetime().nullable().default(null),
    approvedBy: z.uuid().nullable().default(null),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .refine(
    (data) => {
      if (data.keepAudio && data.audioPath === null) return false;
      if (!data.keepAudio && data.audioPath !== null) return false;
      return true;
    },
    {
      message: "keepAudio doit être cohérent avec la présence d'audioPath",
      path: ["keepAudio"],
    },
  );

export type Capture = z.infer<typeof CaptureSchema>;

export function parseCapture(input: unknown): Capture {
  return CaptureSchema.parse(input);
}
